import React from 'react';
import { SelectTypeAhead } from '@dmm/lib-react-typeahead';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { formatPrice } from '@dmm/lib-common/lib/formatting';
import ValidatedInputTrident from './components/ValidatedInputTrident';
import { LEADS_TRIDENT_API_URL } from '../../constants/BoatLoans';
import { clearEmptyProps } from '../../utils/commonHelper';
import { getBoatsConstants } from '../../constants/boats';
import messages from '../../utils/translationMessages';
import * as httpClient from '../../utils/httpClient';
import { formatMessage } from '../../utils/language';
import { setSubmittedPreQualifyForm } from '../../store/actions/tridentApi';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import './styles.css';

const { US_STATES } = getBoatsConstants();
const { form } = messages.boatLoansLeads.getPreQualified;

const MIN_LOAN_AMOUNT = 25000;
class GetPreQualifiedForm extends React.Component {
  states = US_STATES.map((state) => {
    return state.name;
  });

  ficoScores = this.props.ficoScores || [];

  setInitialState() {
    this.ficoScores.push(formatMessage(form.belowScore));
    this.ficoScores = [...new Set(this.ficoScores)];
    this.purchaseTimeframes = [
      formatMessage(form.purchaseTimeFrameRightAway),
      formatMessage(form.purchaseTimeFrame3Months),
      formatMessage(form.purchaseTimeFrameWithin3Months),
      formatMessage(form.purchaseTimeFrameGreater6Months)
    ];

    return {
      formValid: {
        loanAmount: false,
        ficoScore: false,
        name: false,
        email: false,
        phone: false,
        state: false,
        purchaseTimeframe: false
      },
      formFields: {
        loanAmount: '',
        ficoScore: formatMessage(form.belowScore),
        name: '',
        email: '',
        phone: '',
        state: 'none',
        purchaseTimeframe: 'none',
        marketingOptIn: false
      },
      formPristine: {
        loanAmount: true,
        ficoScore: true,
        name: true,
        email: true,
        phone: true,
        state: true,
        purchaseTimeframe: true
      },
      validationErrors: false,
      showSuccess: false,
      showError: false,
      submitted: false
    };
  }
  state = this.setInitialState();

  updateField = (field, valid, value) => {
    if (valid) {
      this.setState({
        formValid: {
          ...this.state.formValid,
          [field]: valid
        },
        formFields: {
          ...this.state.formFields,
          [field]: value
        },
        formPristine: {
          ...this.state.formPristine,
          [field]: false
        }
      });
    } else {
      this.setState({
        formValid: {
          ...this.state.formValid,
          [field]: valid
        },
        formPristine: {
          ...this.state.formPristine,
          purchaseTimeframe: false,
          [field]: false
        },
        validationErrors: true
      });
    }
  };

  formIsInvalid() {
    const invalid = Object.values(this.state.formValid).filter((value) => {
      return !value;
    });

    const isInvalid = invalid.length > 0;

    return isInvalid;
  }

  removeLastOccurrence = (str, char) => {
    const lastIndexOfL = str.lastIndexOf(char);
    return str.slice(0, lastIndexOfL) + str.slice(lastIndexOfL + 1);
  };

  validateLoanAmount = (loanAmount) => {
    if (!loanAmount) {
      return false;
    }
    const purchasePriceNumber = Number(loanAmount.replace(/[^0-9.-]+/g, ''));
    if (purchasePriceNumber < MIN_LOAN_AMOUNT) {
      return false;
    }
    return true;
  };

  setLoanAmount = (event) => {
    const { name, value } = event.target;
    let processedValue = value;
    const numOfPeriods = (value.match(/\./g) || []).length;
    let decimalPart = '';
    if (numOfPeriods > 1) {
      processedValue = this.removeLastOccurrence(processedValue, '.');
    }
    decimalPart =
      processedValue.indexOf('.') > -1
        ? processedValue.substring(processedValue.indexOf('.'))
        : '';
    processedValue =
      formatPrice(
        processedValue.replace(/[^0-9.]/g, '').replace(/\..*/, ''),
        'USD',
        'en-US'
      ) + decimalPart.replace(/[^0-9.]/g, '');
    this.updateField(name, true, processedValue);
    let isValid = this.validateLoanAmount(processedValue);
    this.updateField(name, isValid, processedValue);
  };

  findStateAbbreviation(state) {
    const stateIndex = US_STATES.findIndex((item) => item.name === state);
    if (stateIndex >= 0) {
      return US_STATES[stateIndex].value;
    }
    return '';
  }

  selectChanger = (value) => {
    let state = this.findStateAbbreviation(value);
    this.updateField('state', this.isStateValid(value), state);
  };

  isStateValid(value) {
    const stateIndex = this.states.findIndex((item) => item === value);
    return stateIndex >= 0;
  }

  ficoScoreChanger = (value) => {
    const isValid = value !== 'Below 600';
    this.updateField('ficoScore', isValid, value);
  };

  purchaseTimeframeChanger = (value) => {
    const isValid = value !== 'none';
    this.updateField('purchaseTimeframe', isValid, value);
  };

  setSubmitError(isError) {
    this.setState({
      ...this.state,
      showError: isError
    });
  }

  isValidEngineType(engineType) {
    return (
      engineType &&
      (engineType.toLowerCase() === 'gas' ||
        engineType.toLowerCase() === 'diesel' ||
        engineType.toLowerCase() === 'gasoline')
    );
  }

  handleToggleMarketingOptIn() {
    this.setState({
      ...this.state,
      formFields: {
        ...this.state.formFields,
        marketingOptIn: !this.state.formFields.marketingOptIn
      }
    });
  }

  doSubmit = async (event) => {
    event.preventDefault();

    this.setSubmitError(false);
    const {
      loanAmount,
      ficoScore,
      name: fullName,
      state,
      email,
      phone,
      purchaseTimeframe,
      marketingOptIn
    } = this.state.formFields;

    let [firstName, lastName] = fullName.split(' ');

    // When the lastName is empty, then use the firstName as the lastName. The
    // lastName field is required in Salesforce.
    if (!lastName?.trim()) {
      lastName = firstName;
      firstName = null;
    }

    let data = {
      loanAmount: Number(loanAmount.replace(/[^0-9.-]+/g, '')),
      ficoScore,
      firstName,
      lastName,
      state,
      email,
      phone,
      purchaseTimeframe,
      marketingOptIn,
      leadSource: 101442,
      leadPage: this.props.leadPage
    };

    if (this.props.listing) {
      const {
        year,
        model,
        location,
        make,
        type: boatType,
        owner,
        price,
        id: listingId,
        portalLink: listingURL,
        specifications,
        propulsion,
        fuelType: engineType
      } = this.props.listing;

      const monthlyPayment = parseInt(
        this.props.monthlyPrice.replace(/,/g, '')
      );
      const advertisedRate = this.props.interestRate;
      const purchasePrice =
        !price.hidden && price?.type?.amount && price?.type?.amount.USD > 1
          ? price?.type?.amount.USD
          : undefined;
      const { name: sellerName, boattraderId } = owner;
      const { city: listingCity, state: listingState } = location.address;
      const length = specifications.dimensions.lengths.nominal.ft;
      const engineCount = propulsion?.engines?.length || 0;
      const manufacturer = encodeURIComponent(make);
      const boatName = `${manufacturer} ${model}`;
      const sellerPartyId = parseInt(boattraderId);
      const seller = encodeURIComponent(sellerName);

      data = {
        ...data,
        advertisedRate,
        year,
        model,
        manufacturer,
        boatType,
        boatName,
        length,
        seller,
        sellerPartyId,
        monthlyPayment,
        listingCity,
        listingState,
        listingId,
        listingURL,
        engineCount,
        ...(this.isValidEngineType(engineType) && {
          engineType: engineType.replace('gasoline', 'gas')
        }),
        purchasePrice
      };
    }

    const leadData = clearEmptyProps(data);
    const urlLead = LEADS_TRIDENT_API_URL;
    let apiClient = httpClient.tridentLeadHttpClient();
    try {
      await apiClient.post(urlLead, leadData);
      this.props.setSubmittedPreQualifyForm();
      this.props.onSuccess();
      window.dataLayer.push({
        'event': 'boat loan lead submission',
        requestId: ''
      });
    } catch (e) {
      this.setSubmitError(true);
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  render() {
    return (
      <>
        <div id="prequalified-form" className="prequalified-form">
          <div className="prequalified-instructions">
            {formatMessage(form.instruction)}
          </div>
          <form
            noValidate
            onSubmit={(event) => this.doSubmit(event)}
            className={classnames('form', {
              error: this.state.validationErrors
            })}
          >
            <fieldset
              className={classnames('fieldset fieldset-loanAmount', {
                error: !this.state.formValid.loanAmount,
                selected: this.state.formFields.loanAmount !== 'none'
              })}
              htmlFor="loanAmount"
            >
              <input
                type="text"
                id="loanAmount"
                name="loanAmount"
                placeholder={formatMessage(form.loanAmount)}
                onChange={(event) => this.setLoanAmount(event)}
                value={this.state.formFields.loanAmount}
                aria-label="loan amount"
              />
            </fieldset>
            {!this.state.formValid.loanAmount &&
              !this.state.formPristine.loanAmount && (
                <div className="message-error">
                  {formatMessage(form.loanAmountError)}
                </div>
              )}
            <fieldset
              className={classnames('fieldset fieldset-ficoScore', {
                error: !this.state.formValid.ficoScore,
                selected: this.state.formFields.ficoScore !== 'Below 600'
              })}
              htmlFor="ficoScore"
            >
              <SelectTypeAhead
                list={this.ficoScores}
                label="ficoScore"
                onChange={(event) => this.ficoScoreChanger(event)}
                inputId="ficoScore"
                placeholderText={formatMessage(form.ficoScore)}
              />
            </fieldset>
            {!this.state.formValid.ficoScore &&
              !this.state.formPristine.ficoScore && (
                <div className="message-error pt-5">
                  {formatMessage(form.ficoScoreError)}
                </div>
              )}
            <ValidatedInputTrident
              type="text"
              name="name"
              id="name"
              displayLabel={false}
              labelText="Name"
              placeholder={formatMessage(form.name)}
              onChange={(field, valid, value) =>
                this.updateField(field, valid, value)
              }
              validation="fullName"
            />
            {!this.state.formValid.name &&
              (!this.state.formPristine.name || this.state.submitted) && (
                <div className="message-error">
                  {formatMessage(form.nameError)}
                </div>
              )}

            <fieldset
              className={classnames('fieldset fieldset-state', {
                error: !this.state.formValid.state,
                selected: this.state.formFields.state !== 'none'
              })}
              htmlFor="state"
            >
              <SelectTypeAhead
                list={this.states}
                onChange={(event) => this.selectChanger(event)}
                inputId="state"
                placeholderText={formatMessage(form.state)}
                label="state"
              />
            </fieldset>

            {!this.state.formValid.state &&
              (!this.state.formPristine.state || this.state.submitted) && (
                <div className="message-error pt-5">
                  {formatMessage(form.stateError)}
                </div>
              )}

            <ValidatedInputTrident
              type="text"
              name="email"
              id="email"
              displayLabel={false}
              labelText="Email"
              placeholder={formatMessage(form.email)}
              onChange={(field, valid, value) =>
                this.updateField(field, valid, value)
              }
              validation="email"
            />

            {!this.state.formValid.email &&
              (!this.state.formPristine.email || this.state.submitted) && (
                <div className="message-error">
                  {formatMessage(form.emailError)}
                </div>
              )}

            <ValidatedInputTrident
              type="text"
              name="phone"
              id="phone"
              displayLabel={false}
              labelText="Phone"
              placeholder={formatMessage(form.phone)}
              onChange={(field, valid, value) =>
                this.updateField(field, valid, value)
              }
              validation="phoneNumber"
            />
            {!this.state.formValid.phone &&
              (!this.state.formPristine.phone || this.state.submitted) && (
                <div className="message-error">
                  {formatMessage(form.phoneError)}
                </div>
              )}

            <fieldset
              className={classnames('fieldset fieldset-purchaseTimeframe', {
                error: !this.state.formValid.purchaseTimeframe,
                selected: this.state.formFields.purchaseTimeframe !== 'none'
              })}
              htmlFor="purchaseTimeframe"
            >
              <SelectTypeAhead
                list={this.purchaseTimeframes}
                label="purchaseTimeframe"
                onChange={(event) => this.purchaseTimeframeChanger(event)}
                inputId="purchaseTimeframe"
                placeholderText={formatMessage(form.purchaseTimeframe)}
              />
            </fieldset>

            {!this.state.formValid.purchaseTimeframe &&
              !this.state.formPristine.purchaseTimeframe && (
                <div className="message-error pt-5">
                  {formatMessage(form.purchaseTimeframeError)}
                </div>
              )}

            <div
              className={classnames('message-error submit-error', {
                hidden: !this.state.showError
              })}
            >
              {formatMessage(form.formError)}
            </div>

            <button
              disabled={this.formIsInvalid()}
              className="send-request-button"
              data-testid="send-request-prequalified-button"
              id="send-request-prequalified-button"
              type="submit"
            >
              {formatMessage(form.buttonText)}
            </button>

            <div className="pre-qualified-get-info">
              <input
                type="checkbox"
                selected={this.state.formFields.marketingOptIn}
                onChange={() => this.handleToggleMarketingOptIn()}
                aria-label="pre-qualified-get-info"
              />
              <div>{formatMessage(form.marketingOptIn)}</div>
            </div>
          </form>
        </div>
      </>
    );
  }
}

GetPreQualifiedForm.propTypes = {
  listing: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
  monthlyPrice: PropTypes.string,
  interestRate: PropTypes.number,
  leadPage: PropTypes.string,
  ficoScores: PropTypes.array,
  setSubmittedPreQualifyForm: PropTypes.func
};

export default connect(null, (dispatch) =>
  bindActionCreators(
    {
      setSubmittedPreQualifyForm
    },
    dispatch
  )
)(GetPreQualifiedForm);
